import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, Btn, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { procurementAPI } from '../../../utils/api';
import { fmtRp, fmtDate } from '../../../utils/format';

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

export default function ListTab({ onEdit, onNew }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', request_type: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [page, filters]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.request_type) params.append('request_type', filters.request_type);
      params.append('page', page);
      params.append('limit', 10);
      const r = await procurementAPI.getAll('?' + params.toString());
      setRows(r.data || []);
      setTotal(r.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      DRAFT: { bg: '#F3F4F6', color: '#374151' },
      PENDING_APPROVAL: { bg: '#FFFBEB', color: '#D97706' },
      MENUNGGU_SIRUP: { bg: '#EDE9FE', color: '#5B21B6' },
      PROSES_PENGADAAN: { bg: '#DBEAFE', color: '#1E40AF' },
      APPROVED: { bg: '#ECFDF5', color: '#059669' },
      REJECTED: { bg: '#FEF2F2', color: '#DC2626' },
      ORDERED: { bg: '#DBEAFE', color: '#1E40AF' },
      RECEIVED: { bg: '#E9D5FF', color: '#581C87' },
      COMPLETED: { bg: '#ECFDF5', color: '#059669' },
    };
    const c = cfg[status] || { bg: '#F3F4F6', color: '#374151' };
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ minWidth: 180 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Status</label>
          <Select value={filters.status} onChange={e => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
            options={[{ value: '', label: 'Semua' }, { value: 'DRAFT', label: 'Draft' }, { value: 'PENDING_APPROVAL', label: 'Pending' }, { value: 'MENUNGGU_SIRUP', label: 'Menunggu SIRUP' }, { value: 'PROSES_PENGADAAN', label: 'Proses Pengadaan' }, { value: 'APPROVED', label: 'Approved' }, { value: 'REJECTED', label: 'Ditolak' }, { value: 'ORDERED', label: 'Ordered' }, { value: 'RECEIVED', label: 'Received' }, { value: 'COMPLETED', label: 'Completed' }]} />
        </div>
        <div style={{ minWidth: 150 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Tipe</label>
          <Select value={filters.request_type} onChange={e => { setFilters({ ...filters, request_type: e.target.value }); setPage(1); }}
            options={[{ value: '', label: 'Semua' }, { value: 'ASSET', label: 'Asset' }, { value: 'INVENTORY', label: 'Inventory' }]} />
        </div>
        <Btn onClick={load}>Cari</Btn>
        <Btn variant="ghost" onClick={() => { setFilters({ status: '', request_type: '' }); setPage(1); }}>Reset</Btn>
        <div style={{ marginLeft: 'auto' }}>
          <Btn icon={<Plus size={14}/>} onClick={onNew}>PR Baru</Btn>
        </div>
      </div>

      <Card>
        {error && <div style={{ marginBottom: 12 }}><ErrBox msg={error} onRetry={load} /></div>}
        {loading ? <Spinner /> : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <p style={{ margin: 0 }}>Belum ada procurement request</p>
          </div>
        ) : (
          <>
            <Table pageSize={10} cols={[
              { key: 'request_no', label: 'No. PR', nowrap: true, render: (v, row) => <span style={{ fontWeight: 600, color: '#2563EB', cursor: 'pointer' }} onClick={() => onEdit(row.id)}>{v}</span> },
              { key: 'request_date', label: 'Tanggal', render: v => fmtDate(v) },
              { key: 'request_type', label: 'Tipe', align: 'center', render: v => v === 'ASSET' ? <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#E9D5FF', color: '#581C87' }}>ASSET</span> : <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#DBEAFE', color: '#1E40AF' }}>INVENTORY</span> },
              { key: 'program_kode', label: 'Program', render: (v, r) => <ProgramBadge v={v}/> },
              { key: 'kegiatan_kode', label: 'Kegiatan', render: (v, r) => <KegiatanBadge v={v}/> },
              { key: 'kro_kode', label: 'KRO', render: (v, r) => <KroBadge v={v}/> },
              { key: 'kode_akun', label: 'Akun', render: (v, r) => (
                <div>
                  <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#374151"}}>{v || '-'}</span>
                  <br/><span style={{fontSize:11,color:"#6B7280"}}>{r.nama_akun || '-'}</span>
                </div>
              )},
              { key: 'unit_name', label: 'Unit' },
              { key: 'total_amount', label: 'Nilai', align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: Number(v) >= 200000000 ? '#DC2626' : '#1E40AF' }}>{fmtRp(v)}</span> },
              { key: 'metode_pengadaan', label: 'Metode', align: 'center', render: v => v ? <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: v === 'TENDER' ? '#FEF2F2' : '#ECFDF5', color: v === 'TENDER' ? '#DC2626' : '#059669' }}>{v === 'TENDER' ? 'TENDER' : 'LANGSUNG'}</span> : '-' },
              { key: 'status', label: 'Status', render: v => getStatusBadge(v) },
              { key: 'vendor_name', label: 'Vendor', render: v => v || '-' },
            ]} data={rows} />
            {total > 10 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <Btn size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Btn>
                <span style={{ padding: '6px 12px', fontSize: 13 }}>Halaman {page} dari {Math.ceil(total / 10)}</span>
                <Btn size="sm" variant="ghost" disabled={page * 10 >= total} onClick={() => setPage(p => p + 1)}>Next</Btn>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}